import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import { Menu, MenuItem, TableContainer, Typography } from '@mui/material';
import { BuildingMap } from 'api-client';
import React from 'react';
import { DoorDataGridTable, DoorTableData } from 'react-components';
import { DoorMode as RmfDoorMode } from 'rmf-models';
import { createMicroApp, MicroAppProps } from './micro-app';
import { RmfAppContext } from './rmf-app';
import { getApiErrorMessage } from './utils';

const isTurnstile = (name: string) => /turnstile/i.test(name);

const DOORS = 0;
const TURNSTILES = 1;

const DoorsTabContext = React.createContext<{
  tab: number;
  setTab: (tab: number) => void;
} | null>(null);

const SOURCE_LABELS = ['Doors', 'Turnstiles'] as const;

function DoorsToolbar() {
  const ctx = React.useContext(DoorsTabContext);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  if (!ctx) return null;

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);
  const handleSelect = (tab: number) => {
    ctx.setTab(tab);
    handleClose();
  };

  return (
    <>
      <Typography
        variant="h6"
        // component="h6"
        id="doors-source-button"
        onClick={handleClick}
        aria-controls={open ? 'doors-source-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          cursor: 'pointer',
        }}
      >
        {SOURCE_LABELS[ctx.tab]}
        <ArrowDropDown fontSize="small" />
      </Typography>
      <Menu
        id="doors-source-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ 'aria-labelledby': 'doors-source-button' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem onClick={() => handleSelect(DOORS)} selected={ctx.tab === DOORS}>
          Doors
        </MenuItem>
        <MenuItem onClick={() => handleSelect(TURNSTILES)} selected={ctx.tab === TURNSTILES}>
          Turnstiles
        </MenuItem>
      </Menu>
    </>
  );
}

function DoorsAppContent() {
  const rmf = React.useContext(RmfAppContext);
  const { tab = DOORS } = React.useContext(DoorsTabContext) ?? {};
  const [buildingMap, setBuildingMap] = React.useState<BuildingMap | null>(null);
  const [doorTableData, setDoorTableData] = React.useState<Record<string, DoorTableData>>({});

  React.useEffect(() => {
    if (!rmf) {
      return;
    }
    const sub = rmf.buildingMapObs.subscribe(setBuildingMap);
    return () => sub.unsubscribe();
  }, [rmf]);

  React.useEffect(() => {
    if (!rmf) {
      return;
    }

    let doorIndex = 0;
    buildingMap?.levels.map((level) =>
      level.doors.map(async (door) => {
        try {
          const { data } = await rmf.doorsApi.getDoorHealthDoorsDoorNameHealthGet(door.name);
          const { health_status } = data;
          const sub = rmf.getDoorStateObs(door.name).subscribe((doorState) => {
            setDoorTableData((prev) => ({
              ...prev,
              [door.name]: {
                index: doorIndex++,
                doorName: door.name,
                opMode: health_status ?? 'N/A',
                levelName: level.name,
                doorType: door.door_type,
                doorState: doorState,
                onClickOpen: () =>
                  rmf?.doorsApi.postDoorRequestDoorsDoorNameRequestPost(door.name, {
                    mode: RmfDoorMode.MODE_OPEN,
                  }),
                onClickClose: () =>
                  rmf?.doorsApi.postDoorRequestDoorsDoorNameRequestPost(door.name, {
                    mode: RmfDoorMode.MODE_CLOSED,
                  }),
              },
            }));
          });
          return () => sub.unsubscribe();
        } catch (error) {
          throw new Error(getApiErrorMessage(error));
        }
      }),
    );
  }, [rmf, buildingMap]);

  const all = Object.values(doorTableData);
  const doors =
    tab === TURNSTILES
      ? all.filter((d) => isTurnstile(d.doorName))
      : all.filter((d) => !isTurnstile(d.doorName));
  const noRowsLabel = tab === TURNSTILES ? 'No turnstiles available.' : 'No doors available.';

  return (
    <TableContainer>
      <DoorDataGridTable doors={doors} noRowsLabel={noRowsLabel} />
    </TableContainer>
  );
}

const DoorsAppBase = createMicroApp(<DoorsToolbar />, DoorsAppContent);

export const DoorsApp = React.forwardRef<HTMLDivElement, React.PropsWithChildren<MicroAppProps>>(
  function DoorsApp({ children, ...otherProps }, ref) {
    const [tab, setTab] = React.useState(DOORS);
    return (
      <DoorsTabContext.Provider value={{ tab, setTab }}>
        <DoorsAppBase ref={ref} {...otherProps}>
          {children}
        </DoorsAppBase>
      </DoorsTabContext.Provider>
    );
  },
);
